import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { 
  Wallet, 
  Loader2,
  ChevronRight,
  AlertCircle,
  Clock,
  CheckCircle2,
  XCircle,
  Upload,
  Eye,
  TrendingUp,
  FileSignature,
  Pen,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

const InvestmentsPage = () => {
  const { user } = useAuth();
  const { formatCurrency } = useI18n();
  const [commitments, setCommitments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingFor, setUploadingFor] = useState(null);

  useEffect(() => {
    fetchCommitments();
  }, []);

  const fetchCommitments = async () => {
    try {
      const response = await api.get('/commitments');
      setCommitments(response.data.commitments || []);
    } catch (error) {
      console.error('Error fetching commitments:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { className: 'bg-amber-500/10 text-amber-500 border-amber-500/20', label: 'Pendiente', icon: Clock },
      awaiting_signature: { className: 'bg-purple-500/10 text-purple-500 border-purple-500/20', label: 'Pendiente Firma', icon: Pen },
      countersign_pending: { className: 'bg-blue-500/10 text-blue-500 border-blue-500/20', label: 'Firmado - Verificando', icon: FileSignature },
      approved: { className: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20', label: 'Listo para Fondeo', icon: CheckCircle2 },
      funds_pending: { className: 'bg-orange-500/10 text-orange-500 border-orange-500/20', label: 'Esperando Fondos', icon: Upload },
      confirmed: { className: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20', label: 'Confirmado', icon: CheckCircle2 },
      rejected: { className: 'bg-red-500/10 text-red-500 border-red-500/20', label: 'Rechazado', icon: XCircle },
      signature_rejected: { className: 'bg-red-500/10 text-red-500 border-red-500/20', label: 'Firma Rechazada', icon: XCircle },
      cancelled: { className: 'bg-muted text-muted-foreground border-muted', label: 'Cancelado', icon: XCircle },
    };
    const style = styles[status] || styles.pending;
    const Icon = style.icon;
    return (
      <Badge variant="outline" className={style.className}>
        <Icon className="h-3 w-3 mr-1" />
        {style.label}
      </Badge>
    );
  };

  const handleUploadProof = async (commitmentId, file) => {
    setUploadingFor(commitmentId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await api.post(`/commitments/${commitmentId}/upload-proof`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Comprobante subido exitosamente');
      fetchCommitments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir comprobante');
    } finally {
      setUploadingFor(null);
    }
  };

  const totalCommitted = commitments
    .filter(c => ['approved', 'funds_pending', 'confirmed'].includes(c.status))
    .reduce((sum, c) => sum + c.amount, 0);

  const pendingCount = commitments.filter(c => c.status === 'pending').length;
  const confirmedCount = commitments.filter(c => c.status === 'confirmed').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" strokeWidth={1.5} />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="investments-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="heading-2 mb-2">Mis Inversiones</h1>
          <p className="body-text">
            Gestione sus compromisos de inversión y seguimiento de proyectos.
          </p>
        </div>
        <Link to="/app/opportunities">
          <Button className="btn-primary gap-2">
            <TrendingUp className="h-4 w-4" strokeWidth={1.5} />
            Explorar Proyectos
          </Button>
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="card-dashboard">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-normal text-muted-foreground">
              Capital Total Comprometido
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <Wallet className="h-8 w-8 text-primary" strokeWidth={1.5} />
              <span className="text-2xl font-light">{formatCurrency(totalCommitted)}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dashboard">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-normal text-muted-foreground">
              Compromisos Pendientes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <Clock className="h-8 w-8 text-amber-500" strokeWidth={1.5} />
              <span className="text-2xl font-light">{pendingCount}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dashboard">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-normal text-muted-foreground">
              Inversiones Confirmadas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-8 w-8 text-emerald-500" strokeWidth={1.5} />
              <span className="text-2xl font-light">{confirmedCount}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Commitments List */}
      {commitments.length === 0 ? (
        <Card className="card-dashboard">
          <CardContent className="text-center py-16">
            <Wallet className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" strokeWidth={1.5} />
            <h3 className="text-lg font-medium mb-2">No tiene inversiones aún</h3>
            <p className="text-muted-foreground mb-6">
              Explore las oportunidades disponibles y comience a invertir.
            </p>
            <Link to="/app/opportunities">
              <Button className="btn-primary">Ver Oportunidades</Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {commitments.map((commitment) => (
            <Card key={commitment.commitment_id} className="card-dashboard hover:border-primary/30 transition-colors">
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {getStatusBadge(commitment.status)}
                      <span className="text-xs text-muted-foreground">
                        {new Date(commitment.created_at).toLocaleDateString('es-MX')}
                      </span>
                    </div>
                    <h3 className="font-medium mb-1">{commitment.opportunity_title || 'Proyecto de Inversión'}</h3>
                    <p className="text-2xl font-light text-primary">
                      {formatCurrency(commitment.amount, commitment.currency)}
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    {/* Sign Document button for awaiting_signature status */}
                    {commitment.status === 'awaiting_signature' && commitment.signature_request_id && (
                      <Link to={`/app/signatures/${commitment.signature_request_id}`}>
                        <Button className="btn-primary gap-2">
                          <Pen className="h-4 w-4" strokeWidth={1.5} />
                          Firmar Acuerdo
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </Link>
                    )}

                    {/* Upload proof button for approved/funds_pending commitments */}
                    {['approved', 'funds_pending'].includes(commitment.status) && !commitment.proof_of_transfer_doc_id && (
                      <div>
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => {
                            if (e.target.files[0]) {
                              handleUploadProof(commitment.commitment_id, e.target.files[0]);
                            }
                          }}
                          className="hidden"
                          id={`upload-${commitment.commitment_id}`}
                          disabled={uploadingFor === commitment.commitment_id}
                        />
                        <label htmlFor={`upload-${commitment.commitment_id}`}>
                          <Button
                            variant="outline"
                            className="gap-2 cursor-pointer"
                            asChild
                          >
                            <span>
                              {uploadingFor === commitment.commitment_id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Upload className="h-4 w-4" strokeWidth={1.5} />
                              )}
                              Subir Comprobante
                            </span>
                          </Button>
                        </label>
                      </div>
                    )}

                    {commitment.proof_of_transfer_doc_id && (
                      <Badge variant="outline" className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        Comprobante Enviado
                      </Badge>
                    )}

                    {/* View signed document */}
                    {['countersign_pending', 'approved', 'confirmed'].includes(commitment.status) && commitment.signature_request_id && (
                      <Link to={`/app/signatures/${commitment.signature_request_id}`}>
                        <Button variant="outline" size="sm" className="gap-2">
                          <FileSignature className="h-4 w-4" strokeWidth={1.5} />
                          Ver Documento Firmado
                        </Button>
                      </Link>
                    )}

                    <Link to={`/app/opportunities/${commitment.opportunity_slug || commitment.opportunity_id}`}>
                      <Button variant="outline" size="sm" className="gap-2">
                        <Eye className="h-4 w-4" strokeWidth={1.5} />
                        Ver Proyecto
                      </Button>
                    </Link>
                  </div>
                </div>

                {/* Next Step Alert for awaiting_signature */}
                {commitment.status === 'awaiting_signature' && (
                  <Alert className="mt-4 border-purple-500/30 bg-purple-500/5">
                    <FileSignature className="h-4 w-4 text-purple-500" />
                    <AlertDescription className="text-sm text-purple-600">
                      <span className="font-medium">Siguiente paso:</span> Firme su acuerdo de inversión para continuar con el proceso.
                      <Link to={`/app/signatures/${commitment.signature_request_id}`} className="ml-2 underline font-medium">
                        Ir a firmar →
                      </Link>
                    </AlertDescription>
                  </Alert>
                )}

                {/* Status notes */}
                {commitment.status_notes && (
                  <Alert className="mt-4 border-muted">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="text-sm">
                      {commitment.status_notes}
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Risk Disclaimer */}
      <Card className="border-muted bg-muted/30">
        <CardContent className="p-4">
          <p className="text-xs text-muted-foreground">
            <strong>Aviso:</strong> El estado de sus inversiones puede cambiar conforme avanza el proceso de revisión y fondeo. Consulte regularmente esta sección para mantenerse informado.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default InvestmentsPage;
